import { useContext, useEffect, useState } from 'react';
import { useSlate } from 'slate-react';
import { Range } from 'slate';
import { EditorContext } from 'app/state/contexts/EditorContext';

export const useShadowSelectionMarked = (): boolean => {
  const ctx = useContext(EditorContext) as { shadowHost?: HTMLElement & { shadowRoot: ShadowRoot | null } } | null;
  const shadowHost = ctx?.shadowHost;
  const editor = useSlate();
  const [marked, setMarked] = useState(false);

  useEffect(() => {
    const shadowRoot = shadowHost?.shadowRoot;
    if (!shadowRoot) return undefined;

    const update = (event?: Event) => {
      if (event?.type === 'blur') {
        setMarked(false);
        return;
      }
      const root = ((shadowRoot as any).getSelection ? (shadowRoot as any) : shadowRoot.ownerDocument) as {
        getSelection: () => Selection | null;
      };
      const selection = root.getSelection();
      if (!selection) {
        setMarked(false);
        return;
      }
      const isCollapsed = selection.anchorNode === selection.focusNode && selection.anchorOffset === selection.focusOffset;
      setMarked(!isCollapsed);
    };

    const events = ['mouseup', 'keyup'] as const;
    const options = { capture: true } as const;
    events.forEach((e) => shadowRoot.addEventListener(e, update, options));
    events.forEach((e) => window.addEventListener(e, update, options));
    update();
    return () => {
      events.forEach((e) => shadowRoot.removeEventListener(e, update, options));
      events.forEach((e) => window.removeEventListener(e, update, options));
    };
  }, [shadowHost]);

  if (shadowHost?.shadowRoot) return marked;
  return !!(editor.selection && !Range.isCollapsed(editor.selection));
};
